import DemoBadge from '@/components/feature/DemoBadge';
import RevenueLedger from './RevenueLedger';
import {
  REGIONS,
  TOTAL_BY_REGION,
  SPONSORSHIP_TOTAL,
  PROMOTIONS_TOTAL,
  REGISTRATION_FEE_TOTAL,
  REVENUE_TOTAL,
  PIPELINE_FUNNEL,
  formatUSD,
} from './revenueData';

function Stat({ label, value, note, icon }: { label: string; value: string; note?: string; icon: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
      <div className="flex items-center justify-between">
        <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">{label}</p>
        <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-lg"><i className={icon}></i></span>
      </div>
      <p className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-foreground-950 leading-none">{value}</p>
      {note && <p className="mt-2 text-xs text-foreground-500">{note}</p>}
    </div>
  );
}

export default function RevenueDashboard() {
  const maxRegion = Math.max(...REGIONS.map((r) => TOTAL_BY_REGION[r.id]), 1);
  const maxFunnel = Math.max(...PIPELINE_FUNNEL.map((f) => f.count), 1);
  const streams = [
    { label: 'Sponsorships (monthly)', value: SPONSORSHIP_TOTAL, icon: 'ri-megaphone-line' },
    { label: 'Tournament promotions', value: PROMOTIONS_TOTAL, icon: 'ri-trophy-line' },
    { label: 'Registration fees (3%)', value: REGISTRATION_FEE_TOTAL, icon: 'ri-user-received-line' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-2xl md:text-3xl text-foreground-950">Revenue operations</h2>
        <DemoBadge label="Sample data — not live" />
      </div>

      <div className="flex items-start gap-3 bg-accent-50 border border-accent-300 border-l-4 border-l-accent-500 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-alert-line"></i></span>
        <p className="text-sm text-foreground-800">
          <strong className="font-semibold">Conceptual Revenue Demo &mdash; No Real Payments, Registrations, or Automated Campaigns.</strong> Every figure on this dashboard is sample data for the sales walkthrough and is not linked to any live system.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <Stat label="Total monthly revenue" value={formatUSD(REVENUE_TOTAL)} note="All streams, all regions" icon="ri-money-dollar-circle-line" />
        <Stat label="Sponsorship MRR" value={formatUSD(SPONSORSHIP_TOTAL)} note="Active sample campaigns" icon="ri-megaphone-line" />
        <Stat label="Tournament promotions" value={formatUSD(PROMOTIONS_TOTAL)} note="One-time promotion orders" icon="ri-trophy-line" />
        <Stat label="Registration fees" value={formatUSD(REGISTRATION_FEE_TOTAL)} note="3% of registrations handled" icon="ri-user-received-line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Revenue by region */}
        <div className="lg:col-span-7 bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
          <div className="flex items-center justify-between gap-3 mb-5">
            <h3 className="font-heading font-bold text-xl text-foreground-950">Revenue by region</h3>
            <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">This month · sample</span>
          </div>
          <div className="flex items-end gap-3 md:gap-5 h-56">
            {REGIONS.map((r) => (
              <div key={r.id} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
                <span className="font-label text-[11px] text-foreground-600">{formatUSD(TOTAL_BY_REGION[r.id])}</span>
                <div className="w-full bg-primary-500 rounded-t-md" style={{ height: `${Math.max((TOTAL_BY_REGION[r.id] / maxRegion) * 100, 3)}%` }}></div>
                <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">{r.short}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Streams + reconciliation */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading font-bold text-xl text-foreground-950">Revenue by stream</h3>
            <ul className="mt-4 space-y-4">
              {streams.map((s) => (
                <li key={s.label}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-foreground-800"><span className="w-4 h-4 flex items-center justify-center text-primary-500"><i className={s.icon}></i></span>{s.label}</span>
                    <span className="font-label font-semibold text-foreground-700">{formatUSD(s.value)}</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full bg-accent-500 rounded-full" style={{ width: `${(s.value / REVENUE_TOTAL) * 100}%` }}></div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-background-100 border border-background-200 rounded-lg p-5">
            <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-700">Reconciliation</p>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              {formatUSD(SPONSORSHIP_TOTAL)} + {formatUSD(PROMOTIONS_TOTAL)} + {formatUSD(REGISTRATION_FEE_TOTAL)} = <strong className="text-foreground-950">{formatUSD(REVENUE_TOTAL)}</strong>
            </p>
            <p className="mt-2 text-xs text-foreground-500">The per-region totals (sum {formatUSD(Object.values(TOTAL_BY_REGION).reduce((a, b) => a + b, 0))}) equal the total above. All figures derive from the same sample ledger.</p>
          </div>
        </div>
      </div>

      {/* Pipeline funnel */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
          <h3 className="font-heading font-bold text-xl text-foreground-950">Sponsorship pipeline &mdash; this month</h3>
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Sample flow through stages</span>
        </div>
        <ul className="space-y-3">
          {PIPELINE_FUNNEL.map((f) => (
            <li key={f.stage}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-800">{f.stage}</span>
                <span className="font-label font-semibold text-foreground-600">{f.count}</span>
              </div>
              <div className="mt-1.5 h-2.5 rounded-full bg-background-200 overflow-hidden">
                <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(f.count / maxFunnel) * 100}%` }}></div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <RevenueLedger />
    </div>
  );
}